import React, { useState, useEffect } from 'react';
import { Utilities } from '../utils/Utilities';
import { BloggerManager } from '../utils/BloggerManager';
import type { BloggerPost } from '../utils/BloggerManager';

export type BigBlogEntryViewMode = 'default' | 'BigTile';

interface BigBlogEntryProps {
    /** Output format: default (vertical post-entry lg) or BigTile (horizontal tile as on landing). */
    viewMode?: BigBlogEntryViewMode;
    /** If set, load the blog post with this ID. If not set, show a placeholder. */
    blogID?: string;
}

function BigBlogEntry({ viewMode = 'default', blogID }: BigBlogEntryProps) {
    const [post, setPost] = useState<BloggerPost | null>(null);
    const [loading, setLoading] = useState(!!blogID);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!blogID) {
            setLoading(false);
            setPost(null);
            setError(null);
            return;
        }

        const fetchPost = async () => {
            try {
                setLoading(true);
                setError(null);
                const item = await BloggerManager.getInstance().loadBlogPostById(blogID);
                setPost(item);
            } catch (err) {
                setError(err instanceof Error ? err.message : 'An error occurred');
                setPost(null);
            } finally {
                setLoading(false);
            }
        };

        fetchPost();
    }, [blogID]);

    const wrapperClass = viewMode === 'BigTile' ? 'd-lg-flex post-entry' : 'post-entry lg';

    if (!blogID) {
        return (
            <div className={`${wrapperClass} big-blog-entry-placeholder`}>
                {viewMode === 'BigTile' ? (
                    <div className="d-lg-flex w-100">
                        <div className="bg-light rounded me-4 mb-4 mb-lg-0 flex-shrink-0" style={{ width: '280px', height: '180px' }} />
                        <div className="flex-grow-1">
                            <div className="bg-light rounded mb-2" style={{ width: '40%', height: '1em' }} />
                            <div className="bg-light rounded mb-2" style={{ width: '70%', height: '1.25em' }} />
                            <div className="bg-light rounded" style={{ height: '3em' }} />
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="bg-light rounded" style={{ height: '200px' }} />
                        <div className="mt-3 bg-light rounded" style={{ width: '30%', height: '1em' }} />
                        <div className="mt-2 bg-light rounded" style={{ width: '60%', height: '1.25em' }} />
                        <div className="mt-2 bg-light rounded" style={{ height: '3em' }} />
                    </>
                )}
            </div>
        );
    }

    if (loading) {
        return (
            <div className={wrapperClass}>
                <div className="text-center p-4">Loading...</div>
            </div>
        );
    }

    if (error || !post) {
        return (
            <div className={wrapperClass}>
                <div className="text-center p-4 text-danger">Error: {error || 'No post data available'}</div>
            </div>
        );
    }

    const { src: imageUrl, alt: imageAlt } = Utilities.extractImageFromContent(post.content);
    const category = post.labels && post.labels.length > 0 ? post.labels[0] : 'Uncategorized';
    const date = Utilities.formatDate(post.published);
    const title = post.title;
    const description = Utilities.extractDescription(post.content);
    const authorName = post.author.displayName;
    const authorPhoto = Utilities.fixImageUrl(post.author.image.url);
    const linkTo = post.url;

    if (viewMode === 'BigTile') {
        return (
            <div className="d-lg-flex post-entry">
                <a href={linkTo} target="_blank" rel="noopener noreferrer" className="me-4 thumbnail mb-4 mb-lg-0 d-inline-block">
                    <img src={imageUrl} alt={imageAlt || title} className="img-fluid" />
                </a>
                <div>
                    <div className="post-meta">
                        <span className="date">{category}</span> <span className="mx-1">•</span> <span>{date}</span>
                    </div>
                    <h3>
                        <a href={linkTo} target="_blank" rel="noopener noreferrer">{title}</a>
                    </h3>
                    <p>{description}</p>
                    <div className="d-flex align-items-center author">
                        <div className="photo">
                            <img src={authorPhoto} alt={authorName} className="img-fluid" />
                        </div>
                        <div className="name">
                            <h3 className="m-0 p-0">{authorName}</h3>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="post-entry lg">
            <a href={linkTo} target="_blank" rel="noopener noreferrer">
                <img src={imageUrl} alt={imageAlt || title} className="img-fluid" />
            </a>
            <div className="post-meta">
                <span className="date">{category}</span> <span className="mx-1">•</span> <span>{date}</span>
            </div>
            <h2>
                <a href={linkTo} target="_blank" rel="noopener noreferrer">{title}</a>
            </h2>
            <p className="mb-4 d-block">{description}</p>

            <div className="d-flex align-items-center author">
                <div className="photo">
                    <img src={authorPhoto} alt={authorName} className="img-fluid" />
                </div>
                <div className="name">
                    <h3 className="m-0 p-0">{authorName}</h3>
                </div>
            </div>
        </div>
    );
}

export default BigBlogEntry;
